import type { MockNodeType } from './types'

/**
 * Cytoscape can't render React/Lucide components inside a node — it accepts
 * a `background-image` URL. This module emits per-type Lucide-shaped SVGs as
 * inline data URIs so the canvas matches Figma 13043-183460 (icon-in-circle
 * nodes) without any runtime asset fetching.
 *
 * The icon strokes are tinted to the same color as the node's border, which
 * keeps the icon visible against the light tinted background.
 */

interface NodeStyle {
  /** Lucide SVG path/element markup (without the wrapper <svg>) */
  iconPaths: string
  /** Tailwind <hue>-100 fill */
  bg: string
  /** Tailwind <hue>-700 border + icon stroke */
  fg: string
  /** Outer circle diameter in px */
  size: number
  /** Inner Lucide icon viewBox size in px */
  iconSize: number
  /** Legend dot color (Tailwind class) for TypesPanel */
  legendDot: string
}

const LUCIDE_ATTRS =
  'fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"'

// Lucide v0.454 paths — kept inline to avoid a runtime dep on lucide-static.
const ICONS = {
  building2:
    '<path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/>',
  factory:
    '<path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M17 18h1"/><path d="M12 18h1"/><path d="M7 18h1"/>',
  layoutGrid:
    '<rect width="7" height="7" x="3" y="3" rx="1"/><rect width="7" height="7" x="14" y="3" rx="1"/><rect width="7" height="7" x="14" y="14" rx="1"/><rect width="7" height="7" x="3" y="14" rx="1"/>',
  share2:
    '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" x2="15.42" y1="13.51" y2="17.49"/><line x1="15.41" x2="8.59" y1="6.51" y2="10.49"/>',
  component:
    '<path d="M5.5 8.5 9 12l-3.5 3.5L2 12l3.5-3.5Z"/><path d="m12 2 3.5 3.5L12 9 8.5 5.5 12 2Z"/><path d="M18.5 8.5 22 12l-3.5 3.5L15 12l3.5-3.5Z"/><path d="m12 15 3.5 3.5L12 22l-3.5-3.5L12 15Z"/>',
  fileText:
    '<path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" x2="8" y1="13" y2="13"/><line x1="16" x2="8" y1="17" y2="17"/><line x1="10" x2="8" y1="9" y2="9"/>',
  userRound: '<circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 0 0-16 0"/>',
}

const STYLES: Record<MockNodeType, NodeStyle> = {
  enterprise: { iconPaths: ICONS.building2, bg: '#ffedd5', fg: '#c2410c', size: 52, iconSize: 28, legendDot: 'bg-[#d9622b]' },
  site: { iconPaths: ICONS.factory, bg: '#cffafe', fg: '#0e7490', size: 44, iconSize: 24, legendDot: 'bg-[#418faf]' },
  operating_area: { iconPaths: ICONS.factory, bg: '#e0e7ff', fg: '#4338ca', size: 36, iconSize: 20, legendDot: 'bg-[#4e47dd]' },
  operation_unit: { iconPaths: ICONS.factory, bg: '#ffedd5', fg: '#ea580c', size: 36, iconSize: 20, legendDot: 'bg-orange-400' },
  system: { iconPaths: ICONS.layoutGrid, bg: '#fae8ff', fg: '#be185d', size: 24, iconSize: 14, legendDot: 'bg-[#ca3b76]' },
  functional_location: { iconPaths: ICONS.share2, bg: '#dbeafe', fg: '#1d4ed8', size: 24, iconSize: 14, legendDot: 'bg-[#3762e3]' },
  components: { iconPaths: ICONS.component, bg: '#dcfce7', fg: '#15803d', size: 24, iconSize: 14, legendDot: 'bg-[#b136cc]' },
  documents: { iconPaths: ICONS.fileText, bg: '#ecfccb', fg: '#4d7c0f', size: 24, iconSize: 14, legendDot: 'bg-[#73a233]' },
  // Files a person made in the product (transcripts, artifacts), as the Figma legend lists them under Documents.
  user_artifacts: { iconPaths: ICONS.userRound, bg: '#f5f3ff', fg: '#6d28d9', size: 24, iconSize: 14, legendDot: 'bg-violet-500' },
}

const buildIconDataUri = (paths: string, stroke: string): string => {
  // Explicit width/height (matching the viewBox) give the data-URI a
  // deterministic intrinsic size. Without them the SVG is dimensionless, and
  // cytoscape re-rasterizes its background images on every redraw — so while
  // zooming in/out each re-rasterization of a sizeless SVG renders
  // inconsistently and the icons appear to "change". With a fixed intrinsic
  // size every rasterization is identical, so the icon just scales smoothly.
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" stroke="${stroke}" ${LUCIDE_ATTRS}>${paths}</svg>`
  // encodeURIComponent leaves the SVG legible in DevTools and avoids the
  // base64 size overhead — cytoscape accepts data:image/svg+xml,<utf8> fine.
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
}

export interface NodeTypeStyle {
  type: MockNodeType
  /** The raw Lucide path markup, for callers that draw their own <svg> (the D3 canvas). */
  iconPaths: string
  bg: string
  fg: string
  size: number
  iconSize: number
  iconDataUri: string
  legendDot: string
}

export const NODE_TYPE_STYLES: Record<MockNodeType, NodeTypeStyle> = (() => {
  const out = {} as Record<MockNodeType, NodeTypeStyle>
  for (const key of Object.keys(STYLES) as MockNodeType[]) {
    const s = STYLES[key]
    out[key] = {
      type: key,
      iconPaths: s.iconPaths,
      bg: s.bg,
      fg: s.fg,
      size: s.size,
      iconSize: s.iconSize,
      iconDataUri: buildIconDataUri(s.iconPaths, s.fg),
      legendDot: s.legendDot,
    }
  }
  return out
})()

export const NODE_TYPES: MockNodeType[] = Object.keys(NODE_TYPE_STYLES) as MockNodeType[]

/**
 * The selected node's colours, sampled off the design: the ring, icon and
 * label take the teal, the disc a pale wash of it. Shared by the canvas and
 * the minimap so the thumbnail's selected dot matches the graph.
 */
export const SELECTED_NODE_FG = '#34728d'
export const SELECTED_NODE_BG = '#e4f2fd'
